import type { ID, Region, SyncMode } from "@gooner/shared";
import { syncLeaderOf } from "@gooner/shared";

/*
 * The two sync mutations, as pure region-array transforms. The editor's panel and link
 * button and the screen's hover chrome all reshape regions through these, so "what
 * linking does" is defined once — each host only decides where the new array goes
 * (onRegions in the editor, a PUT from the wall).
 */

/**
 * Point a region at another region's playback, or cut it loose.
 *
 * Setting a leader also releases anything following THIS region: a chain is refused by
 * syncLeaderOf anyway, so a region that becomes a follower while others follow it would
 * leave those others rendering black with no way to see why. They are freed instead,
 * which is the outcome they would have wanted said out loud.
 *
 * `source` is left untouched, so unsyncing puts the region back on the feed it had
 * rather than on a default.
 */
export function linkRegions(
  regions: readonly Region[],
  id: ID,
  syncWith: ID | null,
  mode: SyncMode,
): Region[] {
  return regions.map((r) => {
    if (r.id === id) {
      return syncWith
        ? { ...r, syncWith, syncMode: mode }
        : { ...r, syncWith: undefined, syncMode: undefined };
    }
    if (syncWith && r.syncWith === id) {
      return { ...r, syncWith: undefined, syncMode: undefined };
    }
    return r;
  });
}

/**
 * Toggle MIRRORED playback for the sync a region belongs to — the tile mirror
 * button's act, on either surface.
 *
 * Clicked on a FOLLOWER it flips that follower's own mode; clicked on a LEADER it
 * flips every follower the leader has — the leader carries no mode of its own, and
 * "mirror this pair" is what the click means from either side. A mixed group (some
 * flipped, some not) settles to the opposite of its first follower, one answer for
 * the whole group. No sync, no change: the button should not render there at all.
 */
export function toggleMirror(regions: readonly Region[], id: ID): Region[] {
  const target = regions.find((r) => r.id === id);
  if (!target) return [...regions];
  if (target.syncWith) {
    return regions.map((r) =>
      r.id === id
        ? { ...r, syncMode: (r.syncMode ?? "normal") === "flip" ? "normal" : "flip" }
        : r,
    );
  }
  const followers = regions.filter((r) => r.syncWith === id);
  if (followers.length === 0) return [...regions];
  const next: SyncMode =
    (followers[0].syncMode ?? "normal") === "flip" ? "normal" : "flip";
  return regions.map((r) => (r.syncWith === id ? { ...r, syncMode: next } : r));
}

/**
 * The link button's disconnect: both sides keep the SHARED feed and part ways.
 *
 * Deliberately different from the SYNC panel's Unsync, which restores the region's own
 * pre-sync feed (the snapshot the data model keeps for it). This one adopts what the
 * pair was playing, so the picture stays put and only the lockstep ends — each notice
 * says which it does. Clicking a LEADER frees every follower it has, each adopting the
 * same shared feed; `autoOptimize`, the sound pair and the photo hold are copied verbatim
 * so the whole config matches. (Copying `soundOn: undefined` round-trips as absence through the PUT —
 * JSON.stringify drops it — so a silent leader frees silent followers, not muted-by-key
 * ones.)
 */
export function unlinkAdoptShared(regions: readonly Region[], id: ID): Region[] {
  const target = regions.find((r) => r.id === id);
  if (!target) return [...regions];
  const leader = syncLeaderOf(target, regions);
  const shared = leader ?? target;
  return regions.map((r) => {
    const clickedFollower = leader !== null && r.id === id;
    const freedFollower = leader === null && r.syncWith === id;
    if (!clickedFollower && !freedFollower) return r;
    return {
      ...r,
      source: shared.source,
      autoOptimize: shared.autoOptimize,
      adaptiveFraming: shared.adaptiveFraming,
      soundOn: shared.soundOn,
      volume: shared.volume,
      photoSeconds: shared.photoSeconds,
      syncWith: undefined,
      syncMode: undefined,
    };
  });
}
